import { useIsMutating, UseMutateAsyncFunction } from "@tanstack/react-query";
import clsx from "clsx";
import React, { ChangeEvent, useEffect, useState } from "react";
import { shallowEqual, useDispatch } from "react-redux";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import BarnNumberPicker from "components/BarnNumberPicker/BarnNumberPicker";
import { useExamLockerContext } from "components/ExamLockerContext/ExamLockerContext";
import StallNumberPicker from "components/StallNumberPicker/StallNumberPicker";
import ExamNote from "components/VetExamsTable/ExamNote";
import { EFlagPriority } from "components/VetExamsTable/helper";
import RaceDayExamConclusion from "components/VetExamsTable/RaceDayExamConclusion";
import TableCellWrapper from "components/VetExamsTable/TableCellWrapper";
import Row from "components/VetExamsTable/TableRow/Row";
import ActionCellsWrapper from "components/VetExamsTable/TableRow/RowContent/ActionCellsWrapper";
import AssignActionCell from "components/VetExamsTable/TableRow/RowContent/ActionCellsWrapper/AssignActionCell";
import DateCell from "components/VetExamsTable/TableRow/RowContent/DateCell";
import ExamCell from "components/VetExamsTable/TableRow/RowContent/ExamCell/ExamCell";
import ExamStatusCell from "components/VetExamsTable/TableRow/RowContent/ExamStatusCell/ExamStatusCell";
import FlagCell from "components/VetExamsTable/TableRow/RowContent/FlagCell";
import { getUpcomingRaceEntryFlagPriority } from "components/VetExamsTable/TableRow/RowContent/FlagCell/helper";
import RiskFactorScoreCell from "components/VetExamsTable/TableRow/RowContent/RiskFactorScoreCell";
import RowContent from "components/VetExamsTable/TableRow/RowContent/RowContent";
import TrainerCell from "components/VetExamsTable/TableRow/RowContent/TrainerCell/TrainerCell";
import UnAssignExamCell from "components/VetExamsTable/TableRow/RowContent/UnAssignExamCell";
import {
  ERowColumn,
  IExamsTableRowData,
  ITableColumnsContent
} from "components/VetExamsTable/types";
import { useRacehorse360Api, useSetQueryData } from "hooks/api";
import { FirstApiError } from "hooks/api/errors";
import { useAppSelector } from "hooks/redux/useAppSelector";
import {
  addAssignCheckedId,
  removeAssignCheckedId
} from "store/actions/preRaceScreeningPage";
import { getDateInTimeZone } from "utils/date-utils";
import {
  checkCurrentRaceEntriesTableTab,
  getPreRaceExamTableStatus
} from "../../helper";
import { PRE_RACE_ENTRIES_TOTAL_RESULTS_QUERY_KEY } from "../../options";
import {
  BULK_ASSIGN_UPCOMING_RACE_ENTRY_EXAMS_KEY,
  BULK_ASSIGN_UPCOMING_RACE_ENTRY_RACE_DAY_EXAMS_KEY
} from "../../PreRaceScreeningPageHeader/PickerFilter/PickerFilter";
import EntriesTabActionCells from "./EntriesTabActionCells";
import RaceType from "./RaceType";

import useStyles from "./styles";

interface IProps {
  index: number;
  columnsClassName: string;
  currentRowData: IExamsTableRowData;
  columns: string[];
  onOpenExamForm: (
    examId: string,
    status: racehorse360.HorseExamStatus
  ) => void;
}

const PreRaceRow = (props: IProps) => {
  const { index, currentRowData, columnsClassName, columns, onOpenExamForm } =
    props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const { isLocked, pullLocker } = useExamLockerContext();
  const isBulkAssignEntryExamsMutating = useIsMutating({
    mutationKey: [BULK_ASSIGN_UPCOMING_RACE_ENTRY_EXAMS_KEY]
  });
  const isBulkAssignEntryRaceDayExamsMutating = useIsMutating({
    mutationKey: [BULK_ASSIGN_UPCOMING_RACE_ENTRY_RACE_DAY_EXAMS_KEY]
  });

  const [isExamChecked, setIsExamChecked] = useState(false);
  const [isPassChecked, setIsPassChecked] = useState(false);

  const {
    selectedTab,
    facilityBarns,
    assignCheckedIds,
    selectedFacility,
    showAssignedToMeOnly
  } = useAppSelector(state => state.preRaceScreeningPage, shallowEqual);

  const {
    updateUpcomingRaceEntries,
    filterUpcomingRaceEntries,
    refetchUpcomingRaceEntriesIfOrderedByFlag
  } = useSetQueryData();

  const {
    useSetUpcomingRaceEntryFlag,
    useResetUpcomingRaceEntryFlag,
    useSetUpcomingRaceEntryExamNote,
    useResetUpcomingRaceEntryExamNote,
    useRevertQuickPassUpcomingRaceEntryExam,
    useUnassignUpcomingRaceEntryExam,
    useUnassignUpcomingRaceEntryRaceDayExam
  } = useRacehorse360Api();

  const { mutateAsync: setRaceEntryFlag } = useSetUpcomingRaceEntryFlag();
  const { mutateAsync: resetRaceEntryFlag } = useResetUpcomingRaceEntryFlag();
  const {
    mutateAsync: revertQuickPassRaceEntryExam,
    isPending: isQuickPassRaceEntryExamReverting
  } = useRevertQuickPassUpcomingRaceEntryExam();
  const { mutateAsync: setEntryExamNote, isPending: isEntryExamNoteSetting } =
    useSetUpcomingRaceEntryExamNote();
  const {
    mutateAsync: resetEntryExamNote,
    isPending: isEntryExamNoteResetting
  } = useResetUpcomingRaceEntryExamNote();
  const { mutateAsync: unAssignEntryExam, isPending: isEntryExamUnAssigning } =
    useUnassignUpcomingRaceEntryExam();
  const {
    mutateAsync: unAssignRaceDayExam,
    isPending: isRaceDayExamUnAssigning
  } = useUnassignUpcomingRaceEntryRaceDayExam();

  const {
    eventId: preRaceId,
    eventDate: preRaceDate,
    eventNumber: preRaceNumber,
    eventPosition: preRacePosition,
    eventFlag: preRaceFlag,
    eventFacility: preRaceFacility,
    examId,
    examType,
    examStatus,
    examResult,
    examAssignedUser,
    upcomingRace,
    horse,
    claimChange,
    classChange
  } = currentRowData;

  const isAssignChecked = assignCheckedIds.includes(preRaceId);

  useEffect(() => {
    if (isLocked && isAssignChecked) {
      dispatch(removeAssignCheckedId(preRaceId));
    }
  }, [isLocked, isAssignChecked]);

  const {
    isEntriesTab,
    isEntryExamsTab,
    isEntryResultsTab,
    isRaceDayTab,
    isRaceDayResultsTab
  } = checkCurrentRaceEntriesTableTab(selectedTab.value);

  const isResultsTab = [isEntryResultsTab, isRaceDayResultsTab].some(Boolean);

  const shouldShowFlagPriorityIndicator = [preRaceFlag, !isResultsTab].every(
    Boolean
  );
  const shouldShowFlagOnTrack = [
    isEntriesTab,
    horse.hasActualFlagOnTrack
  ].every(Boolean);
  const shouldShowLastExamNote = [
    horse.lastExamNote,
    isEntriesTab || isRaceDayResultsTab
  ].every(Boolean);
  const shouldShowRevertButton = [
    isEntryResultsTab,
    currentRowData.examType ===
      racehorse360.HorseExamType.HORSE_EXAM_TYPE_QUICK_PASS,
    getDateInTimeZone(new Date(), preRaceFacility?.timezone) <
      new Date(currentRowData.eventDate)
  ].every(Boolean);
  const isEntryExamLoading = [
    isEntryExamNoteSetting,
    isEntryExamNoteResetting
  ].some(Boolean);
  const hasTabAssignActionCell = [isEntryExamsTab, isRaceDayTab].some(Boolean);
  const isExamAssigning = [
    isAssignChecked,
    isBulkAssignEntryExamsMutating || isBulkAssignEntryRaceDayExamsMutating
  ].every(Boolean);

  const handleUnAssignDialogConfirm = async () => {
    let actualRequest: UseMutateAsyncFunction<
      Partial<Omit<racehorse360.UpcomingRaceEntry, "toJSON">>,
      FirstApiError,
      | racehorse360.IUnassignUpcomingRaceEntryExamRequest
      | racehorse360.IUnassignUpcomingRaceEntryRaceDayExamRequest,
      unknown
    >;

    if (isEntryExamsTab) {
      actualRequest = unAssignEntryExam;
    } else if (isRaceDayTab) {
      actualRequest = unAssignRaceDayExam;
    }

    await actualRequest?.({ id: preRaceId });

    if (showAssignedToMeOnly) {
      return filterUpcomingRaceEntries(
        [preRaceId],
        PRE_RACE_ENTRIES_TOTAL_RESULTS_QUERY_KEY
      );
    }

    const updatingExamFields = { assignedUser: null, assignedUserId: null };
    const updatingData: racehorse360.IUpcomingRaceEntry = isEntryExamsTab
      ? { entryExam: updatingExamFields }
      : { raceDayExam: updatingExamFields };

    updateUpcomingRaceEntries([preRaceId], updatingData);
  };

  const handleExamButtonClick = async () => {
    onOpenExamForm(examId, examStatus);
  };

  const handleUndoQuickPassConfirm = async () => {
    const revertedRaceEntry = await revertQuickPassRaceEntryExam({
      id: preRaceId
    });

    if (revertedRaceEntry?.id) {
      return filterUpcomingRaceEntries(
        [revertedRaceEntry.id],
        PRE_RACE_ENTRIES_TOTAL_RESULTS_QUERY_KEY
      );
    }
  };

  const handleSetExamNote = async (note: string) => {
    const updatedEntry = await setEntryExamNote({
      id: preRaceId,
      body: note,
      getOptions: {
        select: [
          "entryExamNote.body",
          "entryExamNote.notedBy",
          "entryExamNote.notedOn"
        ]
      }
    });

    updateUpcomingRaceEntries([updatedEntry.id], {
      entryExamNote: updatedEntry.entryExamNote
    });
  };

  const handleResetExamNote = async () => {
    const resetEntry = await resetEntryExamNote({ id: preRaceId });

    updateUpcomingRaceEntries([resetEntry.id], {
      entryExamNote: null
    });
  };

  const handleChangePriority = async (event: ChangeEvent<HTMLInputElement>) => {
    const priority = getUpcomingRaceEntryFlagPriority(
      event.currentTarget.value as EFlagPriority
    );
    let updatedRaceEntry: racehorse360.IUpcomingRaceEntry;

    if (
      priority ===
      racehorse360.UpcomingRaceEntryFlagPriority
        .UPCOMING_RACE_ENTRY_FLAG_PRIORITY_INVALID
    ) {
      updatedRaceEntry = await resetRaceEntryFlag({ id: preRaceId });
    } else {
      updatedRaceEntry = await setRaceEntryFlag({
        id: preRaceId,
        priority,
        getOptions: {
          select: ["flag.priority", "flag.flaggedBy.lastName", "flag.flaggedOn"]
        }
      });
    }

    await updateUpcomingRaceEntries([updatedRaceEntry.id], {
      flag: updatedRaceEntry.flag
    });
    refetchUpcomingRaceEntriesIfOrderedByFlag();
  };

  const handleAssignChange = async (isChecked: boolean) => {
    const locker = await pullLocker();
    const isLocked = locker.data.isLocked;

    if (isChecked && !isLocked) {
      dispatch(addAssignCheckedId(preRaceId));
    } else {
      dispatch(removeAssignCheckedId(preRaceId));
    }
  };

  const handleBarnChange = (horse: racehorse360.IHorse) => {
    updateUpcomingRaceEntries([preRaceId], {
      horse: {
        barn: horse.barn,
        stallNumber: horse.stallNumber
      }
    });
  };

  const handleStallChange = (patch: racehorse360.IHorse) => {
    updateUpcomingRaceEntries([preRaceId], {
      horse: {
        stallNumber: patch.stallNumber
      }
    });
  };

  const entryExamNoteContent = !isResultsTab && (
    <ExamNote
      index={index}
      rowData={currentRowData}
      isLoading={isEntryExamLoading}
      readOnly={isEntryExamsTab || isRaceDayTab}
      onSaveComment={handleSetExamNote}
      onDeleteComment={handleResetExamNote}
      noteLabel={"Entry Exam Note"}
    />
  );

  const raceDayExamConclusionContent = isRaceDayResultsTab && (
    <RaceDayExamConclusion rowData={currentRowData} />
  );

  const actionCells = !isResultsTab && (
    <ActionCellsWrapper
      className={clsx({
        [classes.rowOdd]: index % 2,
        [classes.rowExamChecked]: isExamChecked,
        [classes.rowPassChecked]: isPassChecked,
        [classes.rowAssignChecked]: isAssignChecked
      })}
    >
      {isEntriesTab && (
        <EntriesTabActionCells
          entryId={preRaceId}
          isExamChecked={isExamChecked}
          isPassChecked={isPassChecked}
          onSetIsExamChecked={setIsExamChecked}
          onSetIsPassChecked={setIsPassChecked}
        />
      )}
      {hasTabAssignActionCell && (
        <AssignActionCell
          isAssignChecked={isAssignChecked}
          onAssignChange={handleAssignChange}
          assignedUserId={examAssignedUser?.id}
          examStatus={examStatus}
          isLoading={isExamAssigning}
        />
      )}
    </ActionCellsWrapper>
  );

  const columnsContent: ITableColumnsContent = {
    [ERowColumn.HORSE_AND_TRAINER]: (
      <TrainerCell
        key={`${preRaceId}-trainer`}
        bold={isEntriesTab}
        trainerInfo={horse.trainer}
      />
    ),
    [ERowColumn.RACE_DATE]: (
      <DateCell
        key={`${preRaceId}-race-date`}
        date={preRaceDate}
        bold={isEntriesTab}
      />
    ),
    [ERowColumn.RACE_NUMBER]: (
      <TableCellWrapper
        key={`${preRaceId}-race-number`}
        bold={isEntriesTab}
        centered
      >
        {preRaceNumber}
      </TableCellWrapper>
    ),
    [ERowColumn.RACE_POSITION]: (
      <TableCellWrapper
        key={`${preRaceId}-race-position`}
        bold={isEntriesTab}
        centered
      >
        {preRacePosition}
      </TableCellWrapper>
    ),
    [ERowColumn.RACE_TYPE]: (
      <RaceType
        key={`${preRaceId}-race-type`}
        bold={isEntriesTab}
        race={upcomingRace}
        claimChange={claimChange}
        classChange={classChange}
      />
    ),
    [ERowColumn.UNASSIGN]: (
      <UnAssignExamCell
        key={`${preRaceId}-un-assign`}
        rowData={currentRowData}
        onConfirm={handleUnAssignDialogConfirm}
        isUnAssignSaving={isEntryExamUnAssigning || isRaceDayExamUnAssigning}
      />
    ),
    [ERowColumn.EXAM]: (
      <ExamCell
        key={`${preRaceId}-exam`}
        assignedUserId={examAssignedUser?.id}
        onExamButtonClick={handleExamButtonClick}
      />
    ),
    [ERowColumn.ENTRY_REVIEW]: (
      <ExamStatusCell
        key={`${preRaceId}-exam-type`}
        status={getPreRaceExamTableStatus(examType, examResult)}
        horseName={horse.name}
        eventName={"Race"}
        eventTabName={"Entries"}
        eventDate={preRaceDate}
        shouldShowRevertButton={shouldShowRevertButton}
        onConfirm={handleUndoQuickPassConfirm}
        isRevertDecisionLoading={isQuickPassRaceEntryExamReverting}
      />
    ),
    [ERowColumn.WATCH_LIST]: (
      <TableCellWrapper key={`${preRaceId}-watch-list`}>
        <FlagCell
          flag={preRaceFlag}
          facility={preRaceFacility}
          defaultButtonTitle={"Watch"}
          selectedButtonTitle={"Watching"}
          onChangePriority={handleChangePriority}
        />
      </TableCellWrapper>
    ),
    [ERowColumn.RISK_FACTOR_SCORE]: (
      <RiskFactorScoreCell
        key={`${preRaceId}-risk-factor-score`}
        value={horse.riskFactorScore}
      />
    ),
    [ERowColumn.RACE_DAY_REVIEW]: (
      <TableCellWrapper key={`${preRaceId}-race-day-review`}>
        {getPreRaceExamTableStatus(examType, examResult)}
      </TableCellWrapper>
    ),
    [ERowColumn.BARN]: (
      <TableCellWrapper key={`${preRaceId}-barn`} bold={isEntriesTab}>
        <BarnNumberPicker
          selectedFacilityId={selectedFacility?.id}
          horse={horse}
          options={facilityBarns}
          endActions={true}
          onBarnChange={handleBarnChange}
        />
      </TableCellWrapper>
    ),
    [ERowColumn.STALL]: (
      <TableCellWrapper key={`${preRaceId}-stall`} bold={isEntriesTab}>
        <StallNumberPicker
          selectedFacilityId={selectedFacility?.id}
          horse={horse}
          endActions={true}
          onStallChange={handleStallChange}
        />
      </TableCellWrapper>
    )
  };

  return (
    <>
      <Row
        index={index}
        rowContainerClassName={clsx({
          [classes.rowExamChecked]: isExamChecked,
          [classes.rowPassChecked]: isPassChecked,
          [classes.rowAssignChecked]: isAssignChecked
        })}
        rowContentClassName={columnsClassName}
        rowData={currentRowData}
        examNoteContent={entryExamNoteContent}
        raceDayExamConclusionContent={raceDayExamConclusionContent}
        actionCellsContent={actionCells}
        shouldShowFlagPriorityIndicator={shouldShowFlagPriorityIndicator}
        shouldShowFlagOnTrack={shouldShowFlagOnTrack}
        shouldShowLastExamNote={shouldShowLastExamNote}
        shouldShowExamsTablePPs={isEntriesTab}
      >
        <RowContent
          rowData={currentRowData}
          columns={columns}
          bold={isEntriesTab}
          customColumnsContent={columnsContent}
          extenderFitsTwoColumns={isEntriesTab}
          extenderFitsOneColumns={hasTabAssignActionCell}
          facilityBarns={facilityBarns}
          selectedFacilityId={selectedFacility.id}
        />
      </Row>
    </>
  );
};

export default React.memo(PreRaceRow);
